import { defineProcedure, ProcedureCategory, ProcedureId } from '../../../../ontology.model';

import { models, REP_IDS } from './representations';

// ------------------------------------------------
// TYPE-SAFE PROCEDURES
// ------------------------------------------------

export const proc = {
  calcNth: defineProcedure(models, {
    id: 'proc.arithmetic_calc_nth_term' satisfies ProcedureId,
    name: 'Calculate N-th Term',
    category: ProcedureCategory.Calculation,
    input_ids: [REP_IDS.params],
    output_id: REP_IDS.nthTerm,
    formula_latex: 'u_n = u_1 + (n-1)d',
    dependencies: [],

    execute: (params) => ({
      value: params.u1 + (params.n - 1) * params.d,
      notation: `u_{${params.n}}`,
      expression: `${params.u1} + (${params.n}-1)(${params.d})`,
    }),
  }),

  calcPartialSum: defineProcedure(models, {
    id: 'proc.arithmetic_calc_partial_sum' satisfies ProcedureId,
    name: 'Calculate Partial Sum',
    category: ProcedureCategory.Calculation,
    input_ids: [REP_IDS.params],
    output_id: REP_IDS.partialSum,
    formula_latex: 'S_n = \\frac{n}{2}(2u_1 + (n-1)d)',
    dependencies: [],

    execute: (params) => {
      const value = (params.n / 2) * (2 * params.u1 + (params.n - 1) * params.d);

      return {
        value,
        notation: `S_{${params.n}}`,
        expression: `\\frac{${params.n}}{2}(2(${params.u1}) + (${params.n}-1)(${params.d}))`,
      };
    },
  }),

  calcPartialSumAlt: defineProcedure(models, {
    id: 'proc.arithmetic_calc_partial_sum_alt' satisfies ProcedureId,
    name: 'Calculate Partial Sum (First & Last)',
    category: ProcedureCategory.Calculation,
    input_ids: [REP_IDS.params, REP_IDS.nthTerm],
    output_id: REP_IDS.partialSum,
    formula_latex: 'S_n = \\frac{n}{2}(u_1 + u_n)',
    dependencies: [],

    execute: (params, nthTerm) => {
      const value = (params.n / 2) * (params.u1 + nthTerm.value);

      return {
        value,
        notation: `S_{${params.n}}`,
        expression: `\\frac{${params.n}}{2}(${params.u1} + ${nthTerm.value})`,
      };
    },
  }),

  determineMonotonicity: defineProcedure(models, {
    id: 'proc.arithmetic_determine_monotonicity' satisfies ProcedureId,
    name: 'Determine Monotonicity',
    category: ProcedureCategory.Classifier,
    input_ids: [REP_IDS.params],
    output_id: REP_IDS.monotonicity,
    formula_latex: 'd > 0 \\to Inc, d < 0 \\to Dec, d = 0 \\to Const',
    dependencies: [],

    execute: (p) => {
      if (p.d > 0) {
        return { state: 'INCREASING', description: `Terms increase by ${p.d} each step.` };
      }
      if (p.d < 0) {
        return { state: 'DECREASING', description: `Terms decrease by ${Math.abs(p.d)} each step.` };
      }
      return { state: 'CONSTANT', description: 'All terms are equal.' };
    },
  }),
};

// Export as array for the compiler
export const arithmeticProcedures = Object.values(proc);
